// Client for process maps (apps/api/src/processes/routes.ts). The map's own logic (paths, reading order, the diff,
// who does each step) is shared with the API from @couragemfg/api/processes-model, so the browser and the server agree.
import { request } from "../api";
import type { Area, MapBody, Op, Released, TemplateInfo } from "@couragemfg/api/processes-model";

export type ProcessRow = {
  id: string; docNo: string; title: string; area: string | null; areaName: string; owner: string | null; summary: string;
  rev: string | null; releasedAt: string | null; updatedAt: string; updatedByName: string | null;
  steps: number; lanes: number; scenarios: number; agentSteps: number; changes: number;
};
export type Revision = { rev: string; note: string; releasedByName: string | null; releasedAt: string };
export type Comment = { id: string; stepId: string; body: string; byName: string | null; at: string; mine: boolean };
export type ProcessDetail = {
  id: string; docNo: string; title: string; area: string | null; areaName: string; owner: string | null; summary: string;
  rev: string | null; releasedAt: string | null; releasedByName: string | null; version: number;
  createdByName: string | null; updatedAt: string; updatedByName: string | null;
  docRecord: { number: string; stage: string } | null;
  draft: MapBody; released: Released | null; canEdit: boolean;
  revisions: Revision[]; comments: Comment[]; me: string;
};
export type SaveResult = { version: number; updatedAt: string; draft: MapBody; changes: number };
export type Meta = Partial<Pick<ProcessDetail, "title" | "owner" | "summary" | "area" | "docNo">>;

const B = "/api/processes";
const enc = encodeURIComponent;
export const processes = {
  list: (area?: string) => request<ProcessRow[]>("GET", `${B}${area ? `?area=${enc(area)}` : ""}`),
  templates: () => request<{ templates: TemplateInfo[]; areas: Area[] }>("GET", `${B}/templates`),
  get: (id: string) => request<ProcessDetail>("GET", `${B}/${enc(id)}`),
  create: (b: { title: string; area?: string | null; owner?: string; summary?: string; template?: string; docNo?: string }) => request<ProcessDetail>("POST", B, b),
  save: (id: string, version: number, b: { draft?: MapBody } & Meta) => request<SaveResult>("PUT", `${B}/${enc(id)}`, { version, ...b }),
  remove: (id: string) => request<{ ok: true }>("DELETE", `${B}/${enc(id)}`),
  changes: (id: string, ops: Op[]) => request<{ version: number; applied: number; added: string[]; skipped: string[] }>("POST", `${B}/${enc(id)}/changes`, { ops }),
  release: (id: string, version: number, note?: string) => request<ProcessDetail>("POST", `${B}/${enc(id)}/release`, { version, note }),
  comment: (mapId: string, stepId: string, body: string) => request<Comment>("POST", "/api/process-comments", { mapId, stepId, body }),
  deleteComment: (id: string) => request<{ ok: true }>("DELETE", `/api/process-comments/${enc(id)}`),
};

export const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

// ---------- agent runs (apps/api/src/processes/runs.ts) ----------
export type RunAction = { tool: string; input: unknown; ok: boolean; result: string };
export type RunStep = {
  step: number; wi: string; id: string; name: string; lane: string; doneBy: "agent" | "person";
  status: "received" | "done" | "stopped" | "failed" | "waiting"; summary: string; actions: RunAction[]; flags: string[]; at: string;
};
export type RunRecord = { kind: "sales_order"; number: string; id: string; customer: string; amount: number | null };
export type Handoff = { step: number; wi: string; id: string; name: string; lane: string; owner: string; instruction: string; reason?: string };
export type RunState = "running" | "handed_off" | "done" | "stopped" | "failed";
export type Run = {
  number: string; mapId: string; docNo: string | null; rev: string; title: string; state: RunState;
  input: { kind: "text"; text: string }; steps: RunStep[]; records: RunRecord[]; handoff: Handoff | null;
  model: string | null; inputTokens: number; outputTokens: number; startedByName: string | null; via: string; startedAt: string; finishedAt: string | null;
};
export type RunRow = Pick<Run, "number" | "rev" | "title" | "state" | "records" | "handoff" | "startedByName" | "startedAt" | "finishedAt"> & { flags: number };

const R = "/api/process-runs";
export const runs = {
  list: (process: string) => request<RunRow[]>("GET", `${R}?process=${enc(process)}`),
  get: (number: string) => request<Run>("GET", `${R}/${enc(number)}`),
  sample: (process: string) => request<{ text: string; customer: string; sample: true }>("GET", `${R}/sample?process=${enc(process)}`),
  start: (process: string, po: string) => request<Run>("POST", R, { process, po }),
};
